<template>
  <div>
    <div v-if="fleetRequirements.length">
      <div class="flex flex-row items-center ml-4">
        Years
        <div class="bg-primary rounded-xl ml-2 p-2 font-semibold text-white">
          {{ year }}
        </div>
      </div>
      <div class="bg-white rounded-2xl mt-5 p-5">
        <div class="flex flex-row justify-between mb-4">
          <span class="text-xl font-semibold text-grey">Scenario 1</span>
          <span class="text-xl font-semibold text-grey">No Dry Season</span>
        </div>
        <data-table
          v-if="fleetRequirementData.wet.length"
          is-td-static
          :table-header="fleetRequirementHeaders"
          :table-data="fleetRequirementData.wet"
        >
          <template #td="{ row, header }">
            {{ putOutOrFormatValue(row[header]) }}
          </template>

          <template #extra-row>
            <tr
              class="tr-values vs-table--tr tr-table-state-null extra"
              key="extra"
            >
              <td class="td vs-table--td">
                <span class="items-center">
                  <span class="whitespace-no-wrap w-full"> Quarter </span>
                </span>
              </td>
              <td
                class="td vs-table--td"
                colspan="3"
                v-for="(item, i) of quarters.wet"
                :key="i"
              >
                <span class="items-center">
                  <span class="whitespace-no-wrap w-full text-center rounded-full bg-primary py-2 text-white font-semibold">
                    {{ putOutOrFormatValue(item) }}
                  </span>
                </span>
              </td>
              <td class="td vs-table--td" />
            </tr>
          </template>
        </data-table>
        <no-data v-else />
      </div>
      <div class="bg-white rounded-2xl mt-5 p-5">
        <div class="flex flex-row justify-between mb-4">
          <span class="text-xl font-semibold text-grey">Scenario 2</span>
          <span class="text-xl font-semibold text-grey">Dry Season</span>
        </div>
        <data-table
          v-if="fleetRequirementData.dry.length"
          is-td-static
          :table-header="fleetRequirementHeaders"
          :table-data="fleetRequirementData.dry"
        >
          <template #td="{ row, header }">
            {{ putOutOrFormatValue(row[header]) }}
          </template>

          <template #extra-row>
            <tr
              class="tr-values vs-table--tr tr-table-state-null extra"
              key="extra"
            >
              <td class="td vs-table--td">
                <span class="items-center">
                  <span class="whitespace-no-wrap w-full"> Quarter </span>
                </span>
              </td>
              <td
                class="td vs-table--td"
                colspan="3"
                v-for="(item, i) of quarters.dry"
                :key="i"
              >
                <span class="items-center">
                  <span class="whitespace-no-wrap w-full text-center rounded-full bg-primary py-2 text-white font-semibold">
                    {{ putOutOrFormatValue(item) }}
                  </span>
                </span>
              </td>
              <td class="td vs-table--td" />
            </tr>
          </template>
        </data-table>
        <no-data v-else />
      </div>
      <div class="bg-white rounded-2xl mt-5 p-5">
        <span class="text-xl font-semibold text-grey mb-4">Small Fleets</span>
        <data-table
          v-if="fleetRequirementData.smallFleet.length"
          is-td-static
          :table-header="fleetRequirementHeaders"
          :table-data="fleetRequirementData.smallFleet"
        >
          <template #td="{ row, header }">
            {{ putOutOrFormatValue(row[header]) }}
          </template>
        </data-table>
        <no-data v-else />
      </div>
    </div>
    <div v-else>
      <no-data />
    </div>
  </div>
</template>

<script>
import { monthsShort } from 'moment'
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import NoData from '@/components/noData'
import downloadUtils from '@/utils/downloadUtils'
import { isNumber } from '@/utils/salesPlanUtils'

export default {
  components: {
    DataTable,
    NoData
  },
  props: {
    dateRange: {
      type: Array,
      default: () => []
    },
    isFetch: {
      type: Boolean,
      default: () => false
    },
    isDownload: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      year: '',
      fleetRequirementHeaders: [],
      fleetRequirementData: {
        wet: [],
        dry: [],
        smallFleet: []
      },
      fleetRequirementFormat: {
        wet: [
          {
            key: 'itm_coal',
            barge_requirement: 'ITM Coal'
          },
          {
            key: 'third_party_coal',
            barge_requirement: 'Normal Fleet 3rd Party Coal'
          },
          {
            key: 'total_barge_requirement',
            barge_requirement: 'Total Barge Requirement'
          }
        ],
        dry: [
          {
            key: 'itm_coal',
            barge_requirement: 'ITM Coal'
          },
          {
            key: 'third_party_coal',
            barge_requirement: 'Normal Fleet 3rd Party Coal'
          },
          {
            key: 'total_barge_requirement',
            barge_requirement: 'Total Barge Requirement'
          }
        ],
        smallFleet: [
          {
            key: 'itm_coal',
            barge_requirement: 'ITM Coal'
          }
        ]
      },
      quarters: {
        wet: [],
        dry: []
      }
    }
  },
  mounted() {
    this.transformHeaderKey()
  },
  computed: {
    ...mapGetters({
      fleetRequirements: 'FleetRequirement/fleetRequirements',
      fleetRequirementFiles: 'FleetRequirement/fleetRequirementFiles'
    })
  },
  watch: {
    isFetch: {
      handler(val) {
        if (val) {
          this.fetchRequirement()
        }
      }
    },
    isDownload: {
      handler(val) {
        if (val) {
          this.downloadRequirement()
        }
      }
    },
    fleetRequirements: {
      immediate: true,
      handler(val) {
        if (!this.isEmpty(val)) {
          this.year = this.moment(val[0].data[0].month, ['YYYY-MM']).format('YYYY')
          this.transformData(val)
        }
      }
    },
    fleetRequirementFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `fleet_requirement_${this.year}.xls`)
          this.$store.dispatch('FleetRequirement/resetFleetRequirements')
        }
      }
    }
  },
  methods: {
    transformHeaderKey() {
      this.fleetRequirementHeaders.push({ key: 'barge_requirement', label: 'Barge Requirement' })
      monthsShort().forEach(month => {
        this.fleetRequirementHeaders.push({ key: this.lowerCase(month), label: month })
      })
      this.fleetRequirementHeaders.push({ key: 'avg', label: 'Avg' })
    },
    async fetchRequirement() {
      try {
        await this.$store.dispatch('FleetRequirement/fetchFleetRequirements')
        this.$emit('update:isFetch', false)
      } catch (e) {
        this.$emit('update:isFetch', false)
        this.showFailedNotification(this, 'Failed to fetch fleet requirement, please try again')
      }
    },
    transformData(val) {
      Object.keys(this.fleetRequirementData).forEach(key => {
        const fleetRequirement = val.find(({ scenario }) => this.camelCase(scenario) === key)
        if (!fleetRequirement) {
          return
        }
        this.fleetRequirementData[key] = this.fleetRequirementFormat[key].map(item => {
          const fleetDatas = {}
          fleetRequirement.data.forEach(fleetData => {
            fleetDatas[this.lowerCase(this.moment(fleetData.month, ['YYYY-MM']).format('MMM'))] = fleetData[item.key] || fleetData.value || '-'
          })
          if (item.key === 'total_barge_requirement' || key === 'smallFleet') {
            if (key !== 'smallFleet') {
              this.trasformDataToQuarter(fleetDatas, key)
            }
            return {
              ...item,
              ...fleetDatas,
              avg: Object.values(fleetDatas).reduce((a, b) => a + b, 0) / Object.values(fleetDatas).length
            }
          }
          return {
            ...item,
            ...fleetDatas
          }
        })
      })
    },
    trasformDataToQuarter(data, keyFleet) {
      let quarter = 0
      this.quarters[keyFleet] = []
      Object.entries(data).forEach(([key, value]) => {
        if (this.moment(key, ['MMM']).format('M') % 3 === 0) {
          quarter += value
          this.quarters[keyFleet].push(quarter / 3)
          quarter = 0
        } else {
          quarter += value
        }
      })
    },
    async downloadRequirement() {
      try {
        await this.$store.dispatch('FleetRequirement/downloadFleetRequirements')
        this.$emit('update:isDownload', false)
      } catch (e) {
        this.$emit('update:isDownload', false)
        this.showFailedNotification(this, 'Failed to download fleet requirement, please try again')
      }
    },
    putOutOrFormatValue(value) {
      if (isNumber(value)) {
        return value.toLocaleString('id-ID', { maximumFractionDigits: 0 })
      }
      return value
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/.vs-table--td {
  background-color: #f1f1fd !important;
  > span {
    > span {
      @apply py-2;
      margin-top: -0.3rem;
      margin-bottom: -0.3rem;
    }
  }
}
</style>
